<template>
    <b-modal
        :visible="visible"
        size="xl"
        scrollable
        hide-footer
        :hide-header-close="state.submitting"
        :no-close-on-backdrop="state.submitting"
        :no-close-on-esc="state.submitting"
        @hide="handleHide"
    >
        <template #modal-title>
            {{ translate(
                'Page(ForwardRequest):Component(Text):page-label',
                `Forward request from ${customerName}`,
                { customer: customerName },
            ) }}
        </template>

        <b-overlay
            :show="state.loading || state.submitting"
            variant="white"
            spinner-variant="primary"
            blur="0"
            opacity=".85"
            rounded="sm"
        >
            <b-alert v-if="errorMessage" show variant="danger">
                <div class="alert-body">
                    <span>{{ errorMessage }}</span>
                </div>
            </b-alert>

            <HamletStaticTable
                v-if="state.loaded"
                :columns="fields"
                :items="state.targets"
                border-variant="light"
                card-class="mb-0 forward-request-modal__targets"
            >
                <template #header-label>
                    {{ translate(
                        'Page(ForwardRequest):Component(Text):products-component-label',
                        'Products and components',
                    ) }}
                </template>

                <template #no-records>
                    {{ translate(
                        'Page(ForwardRequest):Component(Text):nothing-selected',
                        'There are no products available for forwarding.',
                    ) }}
                </template>

                <template #cell-checkbox="{ item }">
                    <b-form-checkbox
                        v-if="!item.ongoingRequestDate"
                        v-model="item.selected"
                        :value="true"
                        :unchecked-value="false"
                    />
                </template>

                <template #cell-product="{ item }">
                    <div class="forward-request-modal__primary-text">
                        {{ item.ourName }}
                    </div>
                    <small v-if="item.ourIdentifier" class="text-less-muted">
                        {{ item.ourIdentifier }}
                    </small>
                </template>

                <template #cell-supplier="{ item }">
                    {{ item.supplier ? item.supplier.title : '—' }}
                </template>

                <template #cell-contact="{ item }">
                    <template v-if="supplierContact(item)">
                        <div class="forward-request-modal__primary-text">
                            {{ supplierContact(item).name || '—' }}
                        </div>
                        <small
                            v-if="supplierContact(item).email"
                            class="text-less-muted"
                        >
                            {{ supplierContact(item).email }}
                        </small>
                    </template>
                    <span v-else>—</span>
                </template>

                <template #cell-productName="{ item }">
                    <span v-if="item.hasName">
                        {{ item.supplierName }}
                    </span>
                    <b-form-input
                        v-else
                        v-model="item.supplierName"
                        :state="targetIdentityState(item)"
                        :placeholder="translate(
                            'Page(ReceivedRequests):Component(Grid):field-product-name',
                            'Supplier product name',
                        )"
                    />
                </template>

                <template #cell-productId="{ item }">
                    <span v-if="item.hasIdentifier">
                        {{ item.supplierIdentifier }}
                    </span>
                    <b-form-input
                        v-else
                        v-model="item.supplierIdentifier"
                        :state="targetIdentityState(item)"
                        :placeholder="translate(
                            'Page(ReceivedRequests):Component(Grid):field-product-id',
                            'Supplier product ID',
                        )"
                    />
                </template>

                <template #cell-status="{ item }">
                    <span v-if="item.ongoingRequestDate" class="text-less-muted">
                        {{ item.ongoingRequestDate }}
                    </span>
                    <span v-else>—</span>
                </template>
            </HamletStaticTable>

            <RequestDetails
                v-if="state.loaded"
                ref="requestDetails"
                class="mt-2 mb-0"
                :request="normalizedRequest"
                :hidden-legislations="[]"
                :last-response-date="lastResponseDate"
            />

            <b-card
                v-if="showEmailSettings && state.loaded"
                no-body
                border-variant="light"
                class="mt-2 mb-0"
            >
                <button
                    type="button"
                    class="forward-request-modal__collapse-header"
                    :aria-expanded="emailSettingsExpanded ? 'true' : 'false'"
                    @click="emailSettingsExpanded = !emailSettingsExpanded"
                >
                    <span>
                        {{ translate(
                            'Page(ForwardRequestEmailOptions):Component(Text):page-label',
                            'Email settings',
                        ) }}
                    </span>
                    <feather-icon
                        :icon="emailSettingsExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    />
                </button>

                <b-collapse :visible="emailSettingsExpanded">
                    <div class="p-2 border-top">
                        <RequestEmailOptions
                            class="forward-request-modal__email-options"
                            @initialized="handleEmailOptionsInitialized"
                            @input="handleEmailOptionsChange"
                        />
                    </div>
                </b-collapse>
            </b-card>

            <div class="forward-request-modal__footer">
                <b-button
                    variant="outline-secondary"
                    :disabled="state.submitting"
                    @click="close"
                >
                    {{ translate('Component(Button):cancel', 'Cancel') }}
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="submitDisabled"
                    @click="submit"
                >
                    {{ translate('Component(Button):forward', 'Forward request') }}
                </b-button>
            </div>
        </b-overlay>
    </b-modal>
</template>

<script>
import {
    BAlert,
    BButton,
    BCard,
    BCollapse,
    BFormCheckbox,
    BFormInput,
    BModal,
    BOverlay,
} from 'bootstrap-vue'
import HamletStaticTable from '@core/components/common/HamletStaticTableV1.vue'
import RequestEmailOptions from '@core/components/common/RequestEmailOptions.vue'
import RequestDetails from '@/views/request/RequestDetails.vue'

export default {
    name: 'ForwardRequestModal',
    components: {
        BAlert,
        BButton,
        BCard,
        BCollapse,
        BFormCheckbox,
        BFormInput,
        BModal,
        BOverlay,
        HamletStaticTable,
        RequestDetails,
        RequestEmailOptions,
    },
    props: {
        visible: { type: Boolean, default: false },
        request: { type: Object, required: true },
        state: { type: Object, required: true },
        showEmailSettings: { type: Boolean, default: false },
    },
    data() {
        return {
            emailSettingsExpanded: false,
            emailOptions: { type: 'system', id: null },
            localError: null,
            fields: [
                {
                    key: 'checkbox',
                    label: '',
                    thClass: 'forward-request-modal__selection-column',
                    tdClass: 'forward-request-modal__selection-column',
                },
                {
                    key: 'product',
                    label: this.translate(
                        'Page(ForwardRequest):Component(Text):column-product-or-component',
                        'Product or component',
                    ),
                },
                {
                    key: 'supplier',
                    label: this.translate(
                        'Page(ForwardRequest):Component(SupplierSelector):column-supplier-name',
                        'Supplier',
                    ),
                },
                {
                    key: 'contact',
                    label: this.translate(
                        'Component(Text):contact',
                        'Contact',
                    ),
                },
                {
                    key: 'productName',
                    label: this.translate(
                        'Page(ForwardRequest):Component(SupplierSelector):column-product-name',
                        'Supplier product name',
                    ),
                },
                {
                    key: 'productId',
                    label: this.translate(
                        'Page(ForwardRequest):Component(SupplierSelector):column-product-id',
                        'Supplier product ID',
                    ),
                },
                {
                    key: 'status',
                    label: this.translate(
                        'Page(ForwardRequest):Component(Text):column-ongoing-request',
                        'Ongoing request',
                    ),
                },
            ],
        }
    },
    computed: {
        normalizedRequest() {
            return {
                ...this.request,
                legislations: this.request.legislations || [],
                verificationDocuments: this.request.verificationDocuments || [],
            }
        },
        customerName() {
            return this.request.customer && this.request.customer.title
                ? this.request.customer.title
                : ''
        },
        selectedTargets() {
            return this.state.targets.filter(target => target.selected === true)
        },
        submitDisabled() {
            return this.state.loading
                || this.state.submitting
                || this.selectedTargets.length === 0
                || this.selectedTargets.some(target => (
                    !target.supplierName && !target.supplierIdentifier
                ))
        },
        errorMessage() {
            const error = this.localError || this.state.error

            if (!error) {
                return null
            }

            return error.response && error.response.data && error.response.data.detail
                ? error.response.data.detail
                : error.message || this.translate(
                    'Component(Text):request-failed',
                    'The request could not be completed.',
                )
        },
        lastResponseDate() {
            const requireDays = 10
            const daysToDeadline = Number(this.request.daysToDeadLine)

            if (!Number.isFinite(daysToDeadline) || daysToDeadline < requireDays) {
                const date = new Date()
                date.setDate(date.getDate() + requireDays)
                return date
            }

            const days = daysToDeadline - requireDays
            return this.$moment(this.request.validTo)
                .subtract(days > requireDays ? requireDays : days, 'days')
                .toDate()
        },
    },
    watch: {
        visible(isVisible) {
            if (isVisible) {
                this.localError = null
                this.emailSettingsExpanded = false
            }
        },
    },
    methods: {
        translate(key, fallback, params = {}) {
            const translation = this.$t(key, params)
            return translation && translation !== key ? translation : fallback
        },
        targetIdentityState(target) {
            return !target.supplierName && !target.supplierIdentifier ? false : null
        },
        supplierContact(target) {
            const contact = target.supplier && target.supplier.contact
            if (!contact) {
                return null
            }

            const communicationChannels = Array.isArray(contact.communicationChannels)
                ? contact.communicationChannels
                : []
            const emailChannel = communicationChannels.find(channel => (
                channel && typeof channel.details === 'string' && channel.details.includes('@')
            ))

            return {
                name: contact.name || '',
                email: contact.email || (emailChannel ? emailChannel.details : ''),
            }
        },
        handleEmailOptionsInitialized(type, id = null) {
            this.emailOptions = { type, id }
        },
        handleEmailOptionsChange(type, id = null) {
            this.emailOptions = { type, id }
            this.$store.dispatch('updateSendEmailPreferences', {
                transport: type,
                configuration: id ? { id } : null,
            }).catch(error => {
                this.localError = error
            })
        },
        submit() {
            if (this.submitDisabled || !this.$refs.requestDetails) {
                return
            }

            const request = this.$refs.requestDetails.getData()
            if (this.showEmailSettings) {
                request.emailOptions = { type: this.emailOptions.type }
                if (this.emailOptions.id) {
                    request.emailOptions.id = this.emailOptions.id
                }
            }

            this.localError = null
            this.$emit('submit', {
                request,
                successCallback: () => this.close(),
                failureCallback: error => {
                    this.localError = error
                },
            })
        },
        close() {
            if (!this.state.submitting) {
                this.$emit('close')
            }
        },
        handleHide(event) {
            if (this.state.submitting) {
                event.preventDefault()
                return
            }

            this.$emit('close')
        },
    },
}
</script>

<style lang="scss" scoped>
.forward-request-modal__primary-text {
    color: #2f2c52;
    font-weight: 600;
}

.forward-request-modal__targets::v-deep .forward-request-modal__selection-column {
    width: 3rem;
    min-width: 3rem;
    max-width: 3rem;
    padding-right: .5rem;
    padding-left: 1rem;
    text-align: center;
}

.forward-request-modal__email-options::v-deep > [class*='col-']:first-child .card {
    border: 1px solid #d8d6de;
    border-radius: .357rem;
    background-color: #fff;
}

.forward-request-modal__email-options::v-deep > [class*='col-']:first-child .card:hover {
    border-color: #f58049;
}

.forward-request-modal__collapse-header {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border: 0;
    background: transparent;
    color: #2f2c52;
    font-size: 1.1rem;
    font-weight: 600;
    text-align: left;
}

.forward-request-modal__footer {
    display: flex;
    justify-content: flex-end;
    gap: .75rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #ebe9f1;
}
</style>
